import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass';
import {
  Config, AddBot, FeatureToggle, Analytics, Localization,
} from '../sections';
import {
  Row,
  Heading,
  FormModal,
  Section,
  Loading,
  Stepper,
  Subtitle,
  Link,
  FeatureGate,
  AutomationList,
  Button,
  ConfirmModal,
} from '../components';
import api from '../api';
import { useAppState, useNotification } from '../hooks';

const LINK_CONVERSATION_BUILDER = 'https://developers.liveperson.com/tutorials-guides-getting-started-with-bot-building-overview.html';

const LINK_3RD_PARTY = 'https://developers.liveperson.com/third-party-bots-getting-started.html';

const LINK_SETTING_UP_RECOMMENDATIONS = 'https://developers.liveperson.com/conversation-orchestrator-agent-assist-setting-up-bot-recommendations.html';

const LINK_TEST_RECOMMENDATIONS = 'https://developers.liveperson.com/conversation-orchestrator-agent-assist-testing.html';

function RecommendedActionsBots(props) {
  const [actions, setActions] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [isModalActive, setIsModalActive] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isFetchingList, setIsFetchingList] = useState(null);
  const isMounted = useRef(false);
  const [confirmModalProps, setConfirmModalProps] = useState(null);
  const {
    isLoading: isLoadingAppState, features, accountId: brandId, themeMode,
  } = useAppState();
  const { addNotification } = useNotification();

  const {
    userId,
    handleFileUpload,
    fileReader,
    setFileReaderLoad,
  } = props;

  const confirmDeleteAction = async (id) => {
    if (isMounted.current) {
      setConfirmModalProps({
        show: false,
      });

      setIsDeleting(true);
      try {
        await api.automationLookup.deleteAutomation(brandId, id);
        if (isMounted.current) {
          setActions(actions.filter((item) => item.id !== id));
          addNotification('Action deleted sucessfully', 'success');
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsDeleting(false);
      }
    }
  };

  const deleteAction = async (id) => {
    if (!isDeleting) {
      const action = actions.find((item) => item.id === id);

      if (isMounted.current) {
        setConfirmModalProps({
          show: true,
          payload: id,
          detail: `Are you sure you want to delete "${action.botName}"`,
          onCancel: () => {
            if (isMounted.current) {
              setConfirmModalProps({
                show: false,
              });
            }
          },
          onConfirm: confirmDeleteAction,
        });
      }
    }
  };

  const getAutomations = async () => {
    if (!isFetchingList) {
      if (isMounted.current) {
        setIsFetchingList(true);
      }
      try {
        const data = features?.['maven.automationlookup.v2']
          ? await api.automationLookup.getAutomationsV2(brandId)
          : await api.automationLookup.getAutomations(brandId);
        if (isMounted.current) {
          setActions(data);
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsFetchingList(false);
      }
    }
  };

  const getConfig = async () => {
    if (!isLoadingConfig) {
      setIsLoadingConfig(true);
      try {
        const data = features?.['maven.automationlookup.v2']
          ? await api.automationLookup.getConfigV2(brandId)
          : await api.automationLookup.getConfig(brandId);
        if (isMounted.current) {
          setConfigData({
            ...data,
            scoreThreshold: data.scoreThreshold * 100,
          });
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsLoadingConfig(false);
      }
    }
  };

  const saveConfig = async (changes) => {
    if (!isSaving) {
      setIsSaving(true);
      try {
        if (features?.['maven.automationlookup.v2']) {
          await api.automationLookup.updateConfigV2(brandId, {
            scoreThreshold: configData.scoreThreshold / 100,
            ...changes,
          });
        } else {
          await api.automationLookup.updateConfig(brandId, {
            scoreThreshold: configData.scoreThreshold / 100,
            ...changes,
          });
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsSaving(false);
      }
    }
  };

  const botListOld = actions && actions.map((action, index) => (
    <Row
      themeMode={themeMode}
      key={action.id}
      data={action}
      handleDelete={() => deleteAction(action.id)}
      useIcon
      rowType="list"
      isLast={index + 1 === actions.length}
    />
  ));

  useEffect(() => {
    async function fetchData() {
      await getConfig();
      await getAutomations();
    }
    if (!isLoadingAppState) {
      fetchData();
    }
  }, [isLoadingAppState]); // eslint-disable-line

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const modal = (
    <FormModal show={isModalActive} onClose={() => setIsModalActive(false)}>
      <AddBot
        addNotification={addNotification}
        getAutomations={getAutomations}
        userId={userId}
        brandId={brandId}
        setIsModalActive={setIsModalActive}
        handleFileUpload={handleFileUpload}
        fileReader={fileReader}
        setFileReaderLoad={setFileReaderLoad}
      />
    </FormModal>
  );

  return (
    <Box>
      <Heading>Bot Recommendations</Heading>
      <Subtitle mb={3}>
        Scale agent conversations by recommending bots to take over conversations,
        based on the consumer’s intent
      </Subtitle>
      <Section
        section="bots-getting-started"
        title="Getting started with bot recommendations"
        subtitle="Setting up recommendations requires an active bot"
      >
        <Stepper
          nocheck
          steps={[
            {
              id: 1,
              title: (
                <Box>
                  Build and deploy bots in
                  {' '}
                  <Link href={LINK_CONVERSATION_BUILDER} target="_blank" rel="noopener noreferrer">Conversation Builder</Link>
                  {' '}
                  or
                  {' '}
                  <Link href={LINK_3RD_PARTY} target="_blank" rel="noopener noreferrer">3rd-party application</Link>
                </Box>
              ),
            },
            {
              id: 2,
              title: (
                <Box>
                  <Link href={LINK_SETTING_UP_RECOMMENDATIONS} target="_blank" rel="noopener noreferrer">Set up recommendations</Link>
                </Box>
              ),
            },
            {
              id: 3,
              title: (
                <Box>
                  <Link href={LINK_TEST_RECOMMENDATIONS} target="_blank" rel="noopener noreferrer">Test recommendations</Link>
                </Box>
              ),
            },
          ]}
          activeStep={2}
        />
      </Section>
      <FeatureToggle
        addNotification={addNotification}
        brandId={brandId}
        themeMode={themeMode}
      />
      <FeatureGate feature="maven.feature.analytics">
        <Analytics
          title="Bot recommendation usage"
          subtitle="The number of times an agent added a recommended bot to a conversation"
          addNotification={addNotification}
          brandId={brandId}
          type="bot"
        />
      </FeatureGate>
      {configData ? (
        <Config
          data={configData}
          setConfig={setConfigData}
          saveConfig={() => saveConfig({
            scoreThreshold: configData.scoreThreshold / 100,
          })}
          isSaving={isSaving || isLoadingConfig}
        />
      ) : null}
      <Section
        isLast
        mb={3}
        title="Configure bot recommendations"
        subtitle="Enable bots and select skills to trigger recommendations based on consumer’s intent"
        controls={
          !features?.['maven.automationlookup.v2'] && (
            <Button
              data-testid="add-bot-button"
              variant="primary"
              onClick={() => setIsModalActive(true)}
              mb={3}
            >
              Add bot
            </Button>
          )
        }
      >
        {(isLoadingAppState || isFetchingList !== false) ? (
          <Flex align="center" justifyContent="center" py={3}>
            <Loading size={24} />
          </Flex>
        ) : (
          <Box>
            {actions && (
              <FeatureGate
                feature="maven.automationlookup.v2"
                fallback={botListOld}
              >
                <AutomationList
                  automations={actions}
                  themeMode={themeMode}
                  getAutomations={getAutomations}
                  addNotification={addNotification}
                  brandId={brandId}
                />
              </FeatureGate>
            )}
          </Box>
        )}
      </Section>
      {configData && (
        <Localization
          configData={configData}
          saveConfig={saveConfig}
          isSaving={isSaving}
          isLoading={isLoadingConfig}
        />
      )}
      {modal}
      <ConfirmModal {...confirmModalProps} />
    </Box>
  );
}

RecommendedActionsBots.propTypes = {
  setFileReaderLoad: PropTypes.func.isRequired,
  handleFileUpload: PropTypes.func.isRequired,
  fileReader: PropTypes.object.isRequired,
  userId: PropTypes.string.isRequired,
};

export default RecommendedActionsBots;
